import React, { useCallback } from "react";
import { CircularProgress, useTheme } from "@mui/material";
import { SovosBox, SovosTypography } from "mosaic-react";
import { DataGrid, rows } from "colbi_web_ui/lib/components";
import { DO_NOT_IMPORT } from "../../constants";
import FieldTypeIcon from "../../components/FieldTypeIcon";

const ValidationGrid = ({
    byColumn,
    columnWarnings,
    effectiveWarnings,
    filteredDgItems,
    fixedCells,
    header,
    issuesByClbId,
    loadingIssues,
    mappings,
    mschFields,
    onRowEdit,
    savingCell,
}) => {
    const theme = useTheme();

    const columnHeaderRenderer = useCallback(
        (headerItem) => {
            const csvColumn = headerItem.columnName;
            const displayName = headerItem.headerName || csvColumn;
            const fieldId = mappings[csvColumn];
            const field = fieldId && fieldId !== DO_NOT_IMPORT
                ? mschFields.find((f) => f.id === fieldId)
                : null;

            const colStats = byColumn[csvColumn] || byColumn[csvColumn?.replace(/^CDM_[^.]+\./, "")] || null;
            const errorCount = colStats ? colStats.errors : 0;
            const warningCount = colStats ? colStats.warnings : (effectiveWarnings[csvColumn] ?? 0);
            const hasIssues = errorCount > 0 || warningCount > 0;

            return (
                <SovosBox sx={{ display: "flex", flexDirection: "column", width: "100%", py: 0.5, gap: 0.5 }}>
                    <SovosBox sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {errorCount > 0 && (
                            <SovosBox
                                sx={{
                                    display: "inline-flex",
                                    alignSelf: "flex-start",
                                    alignItems: "center",
                                    px: 0.75,
                                    py: 0.125,
                                    borderRadius: 0.5,
                                    bgcolor: "error.light",
                                }}
                            >
                                <SovosTypography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ color: "error.main", fontSize: 10 }}
                                >
                                    {errorCount} error{errorCount !== 1 ? "s" : ""}
                                </SovosTypography>
                            </SovosBox>
                        )}
                        {warningCount > 0 && (
                            <SovosBox
                                sx={{
                                    display: "inline-flex",
                                    alignSelf: "flex-start",
                                    alignItems: "center",
                                    px: 0.75,
                                    py: 0.125,
                                    borderRadius: 0.5,
                                    bgcolor: "warning.light",
                                }}
                            >
                                <SovosTypography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ color: "warning.dark", fontSize: 10 }}
                                >
                                    {warningCount} warning{warningCount !== 1 ? "s" : ""}
                                </SovosTypography>
                            </SovosBox>
                        )}
                        {!hasIssues && (
                            <SovosBox
                                sx={{
                                    display: "inline-flex",
                                    alignSelf: "flex-start",
                                    alignItems: "center",
                                    px: 0.75,
                                    py: 0.125,
                                    borderRadius: 0.5,
                                    bgcolor: "success.light",
                                }}
                            >
                                <SovosTypography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ color: "success.dark", fontSize: 10 }}
                                >
                                    0 issues
                                </SovosTypography>
                            </SovosBox>
                        )}
                    </SovosBox>
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {field && <FieldTypeIcon type={field.type} size={13} />}
                        <SovosTypography variant="caption" fontWeight={600} noWrap>
                            {field ? field.name : displayName}
                        </SovosTypography>
                    </SovosBox>
                </SovosBox>
            );
        },
        [mappings, mschFields, effectiveWarnings, byColumn]
    );

    const cellHighlight = useCallback(
        (params) => {
            const clbId = params.row?.CLB_ID ?? params.row?.clb_id;
            if (clbId !== undefined && issuesByClbId.size > 0) {
                const cellIssues = issuesByClbId.get(clbId);
                if (cellIssues) {
                    const rawField = params.field;
                    const colIssues = cellIssues.filter(
                        (issue) => issue.column === rawField || `CDM_CustomCSV.${issue.column}` === rawField,
                    );
                    if (colIssues.length > 0) {
                        const hasError = colIssues.some((i) => i.severity === "error");
                        const cellKey = `${params.row?.uniqueId ?? clbId}__${params.field}`;
                        if (!fixedCells.has(cellKey)) {
                            return hasError ? "validation-error-cell" : "validation-warning-cell";
                        }
                    }
                }
                return "";
            }
            if (!(columnWarnings[params.field] > 0)) return "";
            const cellKey = `${params.row?.uniqueId ?? params.id}__${params.field}`;
            return fixedCells.has(cellKey) ? "" : "validation-warning-cell";
        },
        [columnWarnings, fixedCells, issuesByClbId]
    );

    return (
        <>
            {savingCell && (
                <SovosBox sx={{ alignItems: "center", display: "flex", gap: 1, justifyContent: "flex-end", mb: 1 }}>
                    <SovosBox sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
                        <CircularProgress size={14} />
                        <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>Saving…</SovosTypography>
                    </SovosBox>
                </SovosBox>
            )}
            <DataGrid
            id="column-validation-grid"
            items={filteredDgItems}
            header={header}
            card={rows.CellCard}
            status={{ loading: loadingIssues || false }}
            metadata={{ total: filteredDgItems.length }}
            enablePagination
            headerHeight={70}
            columnHeaderRenderer={columnHeaderRenderer}
            showCheckbox={true}
            editMode
            onRowEdit={onRowEdit}
            onCellHighlight={cellHighlight}
            modifierssx={{
                "& .MuiDataGrid-columnHeader": { alignItems: "flex-start", pt: 1 },
                "& .MuiDataGrid-columnHeaderTitle": { whiteSpace: "normal" },
                "& .validation-warning-cell": {
                    backgroundColor: `${theme.palette.warning.light} !important`,
                    borderLeft: `3px solid ${theme.palette.warning.main} !important`,
                },
                "& .validation-error-cell": {
                    backgroundColor: `${theme.palette.error.light} !important`,
                    borderLeft: `3px solid ${theme.palette.error.main} !important`,
                },
            }}
            />
        </>
    );
};

export default ValidationGrid;
